import React, { useState } from 'react';
import { VUKZ } from '../../theme/vukzTheme';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { ChevronDown, ChevronUp, Eye, Lightbulb, Target } from 'lucide-react';

/**
 * StudyChartBlock — Bloco de gráfico didático para estudos
 *
 * Cada bloco responde a uma pergunta e contém:
 *   1. question       — A pergunta que o gráfico responde
 *   2. chart          — Visualização (line / bar / scatter)
 *   3. whatItShows     — "O que mostra" — leitura guiada dos dados
 *   4. whatStandsOut   — "O que chama atenção" — insight principal
 *   5. practicalMeaning — "O que significa na prática" — implicação prática
 *   6. annotation (opt) — Nota explicativa no gráfico
 *
 * Props:
 *   chartType   — 'line' | 'bar' | 'scatter' (default: 'line')
 *   title       — Título do gráfico
 *   question    — Pergunta que o gráfico responde
 *   data        — Array de objetos [{year: 2020, value: 52.3, ...}]
 *   series      — Array de {dataKey, name, color} para múltiplas linhas
 *   xField      — Nome do campo X (default: 'year')
 *   yField      — Nome do campo Y (para gráficos de série única)
 *   formatY     — Função de formatação do eixo Y
 *   formatTooltip — Função de formatação do tooltip
 *   reversedY   — Inverte eixo Y (útil para tempos: menor = melhor)
 *   annotation  — {value, label, color} para ReferenceLine
 *   whatItShows  — String: leitura guiada
 *   whatStandsOut — String: insight principal
 *   practicalMeaning — String: implicação prática
 *   children    — Conteúdo extra abaixo do gráfico (data table, etc.)
 *   height      — Altura do gráfico (default: 350)
 */
const StudyChartBlock = ({
  chartType = 'line',
  title,
  question,
  data = [],
  series = [],
  xField = 'year',
  yField,
  formatY,
  formatTooltip,
  reversedY = false,
  annotation,
  whatItShows,
  whatStandsOut,
  practicalMeaning,
  children,
  height = 350,
  legendBottom = true,
}) => {
  const [expanded, setExpanded] = useState(true);

  const renderChart = () => {
    const commonXAxis = (
      <XAxis
        dataKey={xField}
        tick={{ fontSize: 12, fill: '#6b7280' }}
        tickLine={false}
        axisLine={{ stroke: '#e5e7eb' }}
      />
    );
    const commonYAxis = (
      <YAxis
        tick={{ fontSize: 12, fill: '#6b7280' }}
        tickLine={false}
        axisLine={{ stroke: '#e5e7eb' }}
        tickFormatter={formatY}
        domain={['auto', 'auto']}
        reversed={reversedY}
      />
    );
    const commonTooltip = (
      <Tooltip
        formatter={formatTooltip || ((value, name) => [formatY ? formatY(value) : value, name])}
        labelFormatter={(label) => `${xField === 'year' ? 'Ano' : xField}: ${label}`}
        contentStyle={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          fontSize: '13px',
        }}
      />
    );
    const commonGrid = <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />;
    const commonLegend = legendBottom ? (
      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '12px' }} />
    ) : null;

    const annotationLine = annotation ? (
      <ReferenceLine
        y={annotation.value}
        stroke={annotation.color || '#ef4444'}
        strokeDasharray="4 4"
        label={{
          value: annotation.label,
          position: 'insideTopRight',
          fill: annotation.color || '#ef4444',
          fontSize: 11,
          fontWeight: 600,
        }}
      />
    ) : null;

    if (chartType === 'bar') {
      return (
        <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          {commonGrid}
          {commonXAxis}
          {commonYAxis}
          {commonTooltip}
          {commonLegend}
          {annotationLine}
          {series.length > 0
            ? series.map((s) => (
                <Bar key={s.dataKey} dataKey={s.dataKey} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} />
              ))
            : yField && <Bar dataKey={yField} fill={VUKZ.primary} radius={[4, 4, 0, 0]} />}
        </BarChart>
      );
    }

    if (chartType === 'scatter') {
      return (
        <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          {commonGrid}
          {commonXAxis}
          {commonYAxis}
          {commonTooltip}
          {commonLegend}
          {annotationLine}
          {series.length > 0
            ? series.map((s) => (
                <Scatter key={s.dataKey} name={s.name} data={data} fill={s.color} />
              ))
            : <Scatter name={title} data={data} fill={VUKZ.primary} />}
        </ScatterChart>
      );
    }

    // Default: line
    return (
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        {commonGrid}
        {commonXAxis}
        {commonYAxis}
        {commonTooltip}
        {commonLegend}
        {annotationLine}
        {series.length > 0
          ? series.map((s) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                stroke={s.color}
                strokeWidth={2.5}
                dot={{ r: 4, fill: s.color }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            ))
          : yField && (
              <Line
                type="monotone"
                dataKey={yField}
                stroke={VUKZ.primary}
                strokeWidth={2.5}
                dot={{ r: 4, fill: VUKZ.primary }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            )}
      </LineChart>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Question header */}
      {question && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <p className="text-sm font-bold text-gray-700 flex items-center gap-2">
            <span className="text-lg">❓</span>
            {question}
          </p>
        </div>
      )}

      {/* Chart title + toggle */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={expanded ? 'Recolher gráfico' : 'Expandir gráfico'}
        >
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {expanded && (
        <>
          {/* Chart */}
          <div className="px-4 py-6" style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>

          {/* Interpretation blocks */}
          {(whatItShows || whatStandsOut || practicalMeaning) && (
            <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {whatItShows && (
                <InterpretationCard
                  icon={<Eye className="w-4 h-4" />}
                  label="O que mostra"
                  text={whatItShows}
                  color="#3b82f6"
                  bgColor="#eff6ff"
                />
              )}
              {whatStandsOut && (
                <InterpretationCard
                  icon={<Lightbulb className="w-4 h-4" />}
                  label="O que chama atenção"
                  text={whatStandsOut}
                  color="#f59e0b"
                  bgColor="#fffbeb"
                />
              )}
              {practicalMeaning && (
                <InterpretationCard
                  icon={<Target className="w-4 h-4" />}
                  label="O que significa na prática"
                  text={practicalMeaning}
                  color="#10b981"
                  bgColor="#ecfdf5"
                />
              )}
            </div>
          )}

          {/* Optional extra content (data table, etc.) */}
          {children}
        </>
      )}
    </div>
  );
};

/** Interpretation sub-card — one of the three reading-guide blocks */
const InterpretationCard = ({ icon, label, text, color, bgColor }) => (
  <div className="rounded-xl p-4 border" style={{ backgroundColor: bgColor, borderColor: `${color}20` }}>
    <div className="flex items-center gap-2 mb-2">
      <span style={{ color }}>{icon}</span>
      <span className="text-xs font-bold uppercase tracking-wide" style={{ color }}>
        {label}
      </span>
    </div>
    <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
  </div>
);

export default StudyChartBlock;
